/** Shared pieces of the interview pages: dates in the organization's time zone, and the applicant's link. */
import { INTERVIEW_TOKEN_HEADER, type ErrorResponse } from "@mubin/contracts";
import { ApiError } from "./api";

const fmt = (opts: Intl.DateTimeFormatOptions, tz: string, locale = "ar-SA-u-ca-islamic-umalqura-nu-arab") =>
  new Intl.DateTimeFormat(locale, { ...opts, timeZone: tz });

/** «الأحد» */
export const weekday = (iso: string, tz: string) => fmt({ weekday: "long" }, tz).format(new Date(iso));
/** «٩» (the Hijri day) */
export const hijriDayNum = (iso: string, tz: string) => fmt({ day: "numeric" }, tz).format(new Date(iso));
/** «جمادى الأولى» */
export const hijriMonth = (iso: string, tz: string) => fmt({ month: "long" }, tz).format(new Date(iso));
/** «الأحد ٩ جمادى الأولى» */
export const hijriDate = (iso: string, tz: string) =>
  fmt({ weekday: "long", day: "numeric", month: "long" }, tz).format(new Date(iso));
/** «٧:٢٠ م» */
export const clock = (iso: string, tz: string) =>
  fmt({ hour: "numeric", minute: "2-digit", hour12: true }, tz, "ar-SA-u-nu-arab").format(new Date(iso));
/** The calendar day in the time zone, e.g. 2026-11-08. */
export const dayKey = (iso: string | Date, tz: string) =>
  new Date(iso).toLocaleDateString("en-CA", { timeZone: tz });
/** The hour (0–23) in the time zone. */
export const hourOf = (iso: string, tz: string) =>
  Number(new Date(iso).toLocaleString("en-GB", { hour: "2-digit", hour12: false, timeZone: tz }));
/** Arabic digits for a number. */
export const ar = (n: number) => n.toLocaleString("ar-SA-u-nu-arab");
/** The first letter of a name, for its avatar. */
export const initial = (name: string) => name.trim().replace(/^ال/, "").charAt(0);

/** The applicant's personal token: from the link's fragment (kept out of server logs), then the page's URL. */
export function linkToken(): string | null {
  const t = new URLSearchParams(window.location.hash.slice(1)).get("t");
  return t && t.length >= 20 ? t : null;
}

/** A request with the applicant's personal token. */
export async function withToken<T>(
  path: string,
  token: string,
  init: { method?: "GET" | "POST"; json?: unknown } = {},
): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? "GET",
    headers: {
      [INTERVIEW_TOKEN_HEADER]: token,
      ...(init.json !== undefined && { "content-type": "application/json" }),
    },
    body: init.json !== undefined ? JSON.stringify(init.json) : undefined,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, body as ErrorResponse | null);
  return body as T;
}

/** The booking invitation (owner's wording, 2026-11-01); the administration may edit it before sending. */
export const INVITE_TEXT = `السلام عليكم ورحمة الله وبركاته
حياك الله أ. {الاسم}

شكرًا لتقديمك على التطوع في تعليم القرآن الكريم مع الجمعية الخيرية لتحفيظ القرآن الكريم بمحافظة رماح 🌿
الخطوة التالية مقابلة قصيرة، صوتية عبر الإنترنت.

اختر الوقت المناسب لك من هنا:
{الرابط}

الرابط خاص بك، ويعمل حتى {آخر موعد}.`;
